/**
 * Une rangée de sous-onglets, sous la barre du haut.
 *
 * Le même composant sert aux réglages, à la fiche d'un enfant et à la V2 :
 * *« quand j'appuie sur réglage je veux avoir des sous tabs en dessous »*, puis
 * *« pour un enfant quand je clique sur le travail fourni je veux que les
 * sous-tabs restent visibles »*. Cette seconde demande n'était pas un problème
 * de dessin mais de routage — les écrans d'un enfant étaient des routes
 * **sœurs**, si bien qu'ouvrir « Le travail fourni » remplaçait tout, y compris
 * ce qui aurait dû rester à l'écran pour en sortir.
 *
 * Il vit dans `ui/` depuis le 26 septembre 2026, **déplacé** depuis
 * `parent/ParentTopNav` : la V2 en a besoin et n'a pas le droit d'importer
 * `parent/` (`v2/partition.test.ts`). Le recopier donnerait deux rangées qui
 * finiraient par ne plus se comporter pareil — c'est déjà la raison qui a fait
 * déménager `ui/enfants`, `ui/periode` et `ui/planDuSoir`.
 *
 * `aussi` (27 septembre 2026) : un onglet qui regroupe des écrans dont les
 * adresses ne sont pas sous la sienne. « Vue d'ensemble » est l'adresse de la
 * fiche, et ses sous-onglets (`cahier`, `effort`, `resumes`) ont gardé leurs
 * adresses pour que les liens déjà écrits continuent de mener au même écran ;
 * l'onglet doit pourtant rester allumé sur chacun d'eux.
 */

import { NavLink, useLocation } from "react-router-dom";

export type SousOnglet = { to: string; label: string; end?: boolean; aussi?: string[] };

/** `chemin` est `prefixe` ou se trouve dessous — jamais un simple début de
 *  chaîne, sans quoi `/cahier` allumerait l'onglet sur `/cahiers`. */
export function estSous(chemin: string, prefixe: string): boolean {
  return chemin === prefixe || chemin.startsWith(`${prefixe}/`);
}

export function SousOnglets({ tabs, label }: { tabs: SousOnglet[]; label: string }) {
  const { pathname } = useLocation();

  return (
    <nav className="subtabs" aria-label={label}>
      {tabs.map((tab) => {
        const aussi = (tab.aussi ?? []).some((prefixe) => estSous(pathname, prefixe));
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end ?? false}
            className={({ isActive }) => (isActive || aussi ? "is-active" : "")}
          >
            {tab.label}
          </NavLink>
        );
      })}
    </nav>
  );
}
